import { RENDER_HASH } from 'config'
import { createContext, useContext, Component, JSX } from 'solid-js'
import { isServer } from 'solid-js/web'

// Context to store data fetchers and their results
const ServerDataContext = createContext<Ctx>()

type Value = any | Promise<any>
type CtxData = Map<string, Value>
type Ctx = {
    registry: CtxData
    get(key: string): Value
    set(key: string, val: Value): void
}

export function create_server_data_context(): Ctx {
    const registry = new Map() // key -> data
    return {
        registry,
        get(key) {
            return registry.get(key)
        },
        set(key, val) {
            registry.set(key, val)
        },
    }
}

export const ServerDataProvider: Component<{
    children: JSX.Element
    context: Ctx
}> = P => {
    return (
        <ServerDataContext.Provider value={P.context}>
            {P.children}
        </ServerDataContext.Provider>
    )
}

export function useServerData<T>(
    key: string,
    fetcher: () => Promise<T>,
    onFetch: (data: T) => void
) {
    if (globalThis.RENDERING_FOR_CAHCHE) return

    const ctx = useContext(ServerDataContext)

    if (isServer && ctx) {
        let value = ctx.get(key)
        if (!value) {
            value = fetcher()
            ctx.set(key, value)
        } else if (value instanceof Promise) {
            value.then(onFetch)
        } else {
            onFetch(value)
        }
    }

    if (typeof window !== 'undefined') {
        if (!globalThis.__SSR_DATA__) globalThis.__SSR_DATA__ = {}
        const initialData = globalThis.__SSR_DATA__[key]
        if (initialData) onFetch(initialData)
    }
}

export function ssr_data<T>(key: string, def: T): T {
    if (isServer) {
        const ctx = useContext(ServerDataContext)
        if (!ctx) return def
        return ctx.get(key) || def
    }

    if (RENDER_HASH != globalThis.__SSR_RENDER_HASH__) return def

    if (typeof window !== 'undefined') {
        if (!globalThis.__SSR_DATA__) globalThis.__SSR_DATA__ = {}
        const initialData = globalThis.__SSR_DATA__[key]
        if (initialData) return initialData
    }
    return def
}
